import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LoaderCircle, LockKeyhole, RotateCcw } from 'lucide-react'
import { api } from '../lib/apiClient'
import { formatDate, formatMoney, humanize } from '../lib/formatters'
import type { AppRecord } from '../types'
import { Modal } from './Modal'
import { StatusBadge } from './StatusBadge'

type HistoryEvent = {
  kind: 'sent' | 'feedback' | 'unlocked'
  at: string
  version: number
  outcome?: 'sent' | 'accepted' | 'rejected' | 'superseded'
  note?: string
  revenueNet?: number | null
  grossProfit?: number | null
  grossMarginPct?: number | null
}

export function CustomerQuoteFeedbackSection({ record, reload }: { record: AppRecord; reload: () => Promise<void> }) {
  const sent = Boolean(record.customerQuoteSentAt)
  const archived = String(record.status || '').toLowerCase() === 'superseded'
  const customerQuote = record.quoteType === 'customer' || sent || (!record.supplierId && Array.isArray(record.customerQuotePdf) && record.customerQuotePdf.length > 0)
  const [status, setStatus] = useState(String(record.status || 'draft').toLowerCase())
  const [note, setNote] = useState(String(record.customerFeedbackNotes || ''))
  useEffect(() => {
    setStatus(String(record.status || 'draft').toLowerCase())
    setNote(String(record.customerFeedbackNotes || ''))
  }, [record.status, record.customerQuoteSentAt, record.customerFeedbackNotes])
  const [reason, setReason] = useState('')
  const [confirmUnlock, setConfirmUnlock] = useState(false)
  const [unlockOpen, setUnlockOpen] = useState(false)
  const [working, setWorking] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const history = Array.isArray(record.customerQuoteHistory) ? record.customerQuoteHistory as HistoryEvent[] : []
  if (!customerQuote && !history.length) return null

  const saveFeedback = async () => {
    const currentStatus = String(record.status || '').toLowerCase()
    const currentNote = String(record.customerFeedbackNotes || '').trim()
    if (archived || working || (!sent && status !== 'superseded') || (status === currentStatus && note.trim() === currentNote)) return
    const correcting = status !== currentStatus && ['accepted', 'rejected'].includes(currentStatus)
    if (status === 'superseded' && !note.trim()) { setMessage('Enter a reason before archiving this Quote as superseded.'); return }
    if (correcting && (!note.trim() || note.trim() === currentNote)) { setMessage('Enter a new reason to correct the recorded decision.'); return }
    if (!window.confirm(status === 'superseded' ? 'Archive this Quote as superseded? It will be excluded from Opportunity revenue and cannot be reactivated.' : status === currentStatus ? 'Save the customer feedback note? The sent PDF remains locked.' : 'Record the customer response as ' + humanize(status) + '? The sent PDF remains locked.')) return
    setWorking(true)
    setMessage(null)
    try {
      const result = await api.action<AppRecord>('quotes', record.id, 'customer-feedback', { status, note })
      await reload()
      setMessage(String(result.revenueWarning || 'Customer feedback recorded.'))
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not record customer feedback.') }
    finally { setWorking(false) }
  }

  const unlock = async () => {
    if (!reason.trim() || !confirmUnlock || working) return
    setWorking(true)
    setMessage(null)
    try {
      const result = await api.action<AppRecord>('quotes', record.id, 'unlock-customer-quote', { reason })
      await reload()
      setUnlockOpen(false)
      setReason('')
      setConfirmUnlock(false)
      setMessage(String(result.revenueWarning || 'Quote unlocked. Generate a new PDF version before sending it again.'))
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not unlock the customer Quote.') }
    finally { setWorking(false) }
  }

  const stageSuggestion = status === 'accepted' ? 'Consider marking the Opportunity won.'
    : status === 'rejected' ? 'Review the Opportunity stage; another customer offer may still be active.'
      : 'Consider moving the Opportunity to Quote sent.'

  return <section className="card mb-6 p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-bold text-slate-900">Customer response</h2>
        <p className="mt-1 text-sm text-slate-500">{archived ? 'This Quote is archived. Its documents and history are retained, and it is excluded from current reporting.' : sent ? 'The sent PDF is locked. Record feedback without changing its contents.' : 'Archive this draft as superseded, or generate a PDF before sending it.'}</p>
      </div>
      {customerQuote && <StatusBadge value={record.status} />}
    </div>
    {customerQuote && !archived && <div className="mt-4 grid gap-3 sm:grid-cols-[190px_1fr_auto] sm:items-end">
      <label className="text-sm font-semibold text-slate-700">Quote stage
        <select className="input mt-1 w-full" value={status} disabled={working} onChange={(event) => setStatus(event.target.value)}>
          {!sent && <option value="draft">Draft</option>}{sent && <><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="rejected">Rejected</option></>}<option value="superseded">Superseded / archive</option>
        </select>
      </label>
      <label className="text-sm font-semibold text-slate-700">Feedback / archive reason
        <input className="input mt-1 w-full" value={note} maxLength={2000} disabled={working} onChange={(event) => setNote(event.target.value)} placeholder={status === 'superseded' ? 'Required reason for archiving this Quote' : 'Customer comments or reason for correcting a decision'} />
      </label>
      <button className="btn-primary" disabled={working || (status === String(record.status || '').toLowerCase() && note.trim() === String(record.customerFeedbackNotes || '').trim())} onClick={() => void saveFeedback()}>{working ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}{status === String(record.status || '').toLowerCase() ? 'Save feedback' : 'Save stage'}</button>
    </div>}
    {sent && !archived && <p className="mt-3 text-xs text-slate-500">Sent {formatDate(record.customerQuoteSentAt)}. {stageSuggestion} {Boolean(record.opportunityId) && <Link className="font-semibold text-pine-700 underline" to={'/opportunities/' + record.opportunityId}>Open Opportunity</Link>}</p>}
    {Boolean(record.customerFeedbackNotes) && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">Latest feedback: {String(record.customerFeedbackNotes)}</p>}
    {sent && !archived && <button className="btn-secondary mt-4" disabled={working} onClick={() => setUnlockOpen(true)}><LockKeyhole className="h-4 w-4" />Unlock for revision</button>}
    {message && <p role="status" className="mt-3 text-sm font-semibold text-amber-800">{message}</p>}
    {history.length > 0 && <details className="mt-4 border-t border-slate-100 pt-3">
      <summary className="cursor-pointer text-sm font-semibold text-pine-700">Customer Quote history ({history.length})</summary>
      <ol className="mt-3 space-y-2">{[...history].reverse().map((event, index) => <li key={event.at + '-' + index} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <span className="font-semibold">{humanize(event.kind === 'feedback' ? event.outcome : event.kind)}</span> · v{String(event.version).padStart(3, '0')} · {formatDate(event.at)}
        <span> · {formatMoney(event.revenueNet)} net · {formatMoney(event.grossProfit)} direct-cost profit · {typeof event.grossMarginPct === 'number' ? new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(event.grossMarginPct) + '%' : '—'} margin</span>
        {event.note && <span className="block text-slate-500">{event.note}</span>}
      </li>)}</ol>
    </details>}
    <Modal open={unlockOpen} onClose={() => !working && setUnlockOpen(false)} title="Unlock sent customer Quote?" description="The sent PDF and decision history stay preserved. This Quote becomes an editable draft, and a new PDF version must be generated and sent again.">
      <div className="space-y-4 p-5 sm:p-6">
        <label className="block text-sm font-semibold text-slate-700">Reason for revision
          <textarea className="input mt-1 min-h-24 w-full" value={reason} maxLength={500} disabled={working} onChange={(event) => setReason(event.target.value)} />
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" checked={confirmUnlock} disabled={working} onChange={(event) => setConfirmUnlock(event.target.checked)} />I understand the current customer decision will be cleared and a new PDF must be sent.</label>
        <div className="flex justify-end gap-2"><button className="btn-secondary" disabled={working} onClick={() => setUnlockOpen(false)}>Cancel</button><button className="btn-primary" disabled={working || !reason.trim() || !confirmUnlock} onClick={() => void unlock()}>{working ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}Unlock Quote</button></div>
      </div>
    </Modal>
  </section>
}
